
import * as React from 'react';
import Link from 'next/link';
import { Icon } from '@/components/site/ui';
import { cn } from '@/lib/utils';

const TONE = {
  info: 'border-info/20 bg-info-subtle text-info',
  success: 'border-success-soft-foreground/20 bg-success-soft text-success-soft-foreground',
  warning: 'border-warning/30 bg-warning-subtle text-warning-strong',
  destructive: 'border-destructive/20 bg-destructive-soft text-destructive-soft-foreground',
  neutral: 'border-border bg-muted/50 text-foreground',
} as const;

export function Callout({
  tone = 'info',
  href,
  icon,
  title,
  action,
  children,
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement> & {
  tone?: keyof typeof TONE;
  icon?: string;
  title?: React.ReactNode;
  action?: React.ReactNode;
  /** The whole callout is the way to act on it: a link with a chevron (owner, 2026-11-02). */
  href?: string;
}) {
  // A one-line callout centres its icon on the text; a titled one keeps it at the top.
  const classes = cn(
    'flex gap-3 rounded-xl border px-4 py-3 text-sm',
    title ? 'items-start' : 'items-center',
    TONE[tone],
    href && 'transition-[filter] hover:brightness-[0.98] focus-visible:outline-none focus-visible:shadow-focus',
    className,
  );
  const body = (
    <>
      {icon && <Icon name={icon} size={18} className={cn('shrink-0', title && 'mt-0.5')} />}
      <div className="min-w-0 flex-1">
        {title && <div className="font-semibold">{title}</div>}
        <div className={cn(title && 'mt-0.5', 'leading-relaxed')}>{children}</div>
      </div>
      {action && <div className="shrink-0">{action}</div>}
      {href && <Icon name="arrow-right-01" size={18} className="shrink-0" />}
    </>
  );
  if (href)
    return (
      <Link href={href} className={classes}>
        {body}
      </Link>
    );
  return (
    <div className={classes} {...props}>
      {body}
    </div>
  );
}
